import { ActionIcon, Button, Loader, Modal } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconArrowAutofitWidth, IconDownload, IconMinus, IconPlus } from '@tabler/icons-react';
import { type ReactNode, type RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { zoomText } from '../screens/documents/viewer-core.js';
import { openPdf, type PdfHandle } from '../screens/documents/viewer-engines.js';
import classes from './AttachmentPreview.module.css';
import { blobRangeFetch, loadPreview, type PreviewKind } from './preview-core.js';

/** Yakınlaştırma basamakları; "sığdır" pencereye göre hesaplanır. */
const ZOOM_STEPS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2, 3, 4] as const;
const MIN_ZOOM = 0.25;
const MAX_ZOOM = 4;
/** Bir PDF ekinde çizilen en çok sayfa; fazlası indirilerek okunur. */
const MAX_PDF_PAGES = 50;
/** Tuvalin iç boşluğu (CSS ile aynı): sığdırma hesabında düşülür. */
const CANVAS_PADDING = 16;

type Zoom = 'fit' | number;

type State =
  | { status: 'loading' }
  | { status: 'error'; code: string }
  | { status: 'ready'; kind: PreviewKind; blob: Blob; objectUrl: string };

export interface AttachmentPreviewProps {
  opened: boolean;
  /** Önizlenen ek: ad ve indirme ucunun adresi. */
  file: { fileName: string; url: string };
  onClose: () => void;
  /** Pencerenin "Download" düğmesi (gemi: kaydetme penceresi); yoksa `downloadHref`. */
  onDownload?: (() => void) | undefined;
  downloadHref?: string | undefined;
}

function errorCode(error: unknown): string {
  const code = (error as { code?: unknown } | null)?.code;
  return typeof code === 'string' ? code : 'default';
}

function nextStep(current: number, direction: 1 | -1): number {
  if (direction === 1) return ZOOM_STEPS.find((step) => step > current + 0.001) ?? current;
  return [...ZOOM_STEPS].reverse().find((step) => step < current - 0.001) ?? current;
}

/**
 * Ek önizlemesi (ortak 2026-10-24 madde 4; ADR 0126): resim (JPG, PNG, WebP, GIF) yakınlaştırma
 * ve sığdırmayla, PDF uygulamayla paketli pdf.js ile. Dosya indirme ucundan bir kez okunur ve
 * türü sunucunun başlığından doğrulanır (`loadPreview`); SVG ve diğer türler yalnız indirilir.
 * Dosya bellekte `Blob`, resim `objectURL`'dir; pencere kapanınca bırakılır. Gemide adres gemi
 * sunucusundandır; internet gerekmez. Pencere Esc ile kapanır, odak simgeye döner.
 */
export function AttachmentPreview({
  opened,
  file,
  onClose,
  onDownload,
  downloadHref,
}: AttachmentPreviewProps) {
  const { t, i18n } = useTranslation();
  const p = (key: string, options?: Record<string, unknown>) =>
    t(`attachmentPreview.${key}`, options ?? {});
  const narrow = useMediaQuery('(max-width: 640px)') ?? false;
  const [state, setState] = useState<State>({ status: 'loading' });
  const [zoom, setZoom] = useState<Zoom>('fit');
  const [fitScale, setFitScale] = useState(1);
  const canvas = useRef<HTMLElement | null>(null);
  // Kapalıyken dosya okunmaz; kapanınca bırakılır
  const url = opened ? file.url : null;

  useEffect(() => {
    if (url === null) return;
    const controller = new AbortController();
    let objectUrl: string | null = null;
    setState({ status: 'loading' });
    setZoom('fit');
    loadPreview(url, undefined, controller.signal).then(
      ({ kind, blob }) => {
        if (controller.signal.aborted) return;
        objectUrl = URL.createObjectURL(blob);
        setState({ status: 'ready', kind, blob, objectUrl });
      },
      (error: unknown) => {
        if (!controller.signal.aborted) setState({ status: 'error', code: errorCode(error) });
      },
    );
    // Kapanınca okuma kesilir ve bellekteki dosya bırakılır
    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url]);

  const scale = zoom === 'fit' ? fitScale : zoom;
  const step = (direction: 1 | -1) => setZoom(nextStep(scale, direction));
  const locale = i18n.resolvedLanguage === 'tr' ? 'tr-TR' : 'en-GB';
  const ready = state.status === 'ready';

  const download = onDownload ? (
    <Button
      size="xs"
      variant="white"
      leftSection={<IconDownload size={14} />}
      onClick={onDownload}
      className={classes.download}
    >
      {p('downloadButton')}
    </Button>
  ) : downloadHref ? (
    <Button
      component="a"
      href={downloadHref}
      download={file.fileName}
      size="xs"
      variant="white"
      leftSection={<IconDownload size={14} />}
      className={classes.download}
    >
      {p('downloadButton')}
    </Button>
  ) : null;

  let body: ReactNode;
  if (state.status === 'loading') {
    body = (
      <div className={classes.message} role="status">
        <Loader size="sm" />
        <span>{p('loading')}</span>
      </div>
    );
  } else if (state.status === 'error') {
    body = (
      <div className={classes.message} role="alert">
        <span>
          {t(`attachmentPreview.errors.${state.code}`, {
            defaultValue: p('errors.default'),
          })}
        </span>
      </div>
    );
  } else if (state.kind === 'image') {
    body = (
      <ImageView
        src={state.objectUrl}
        alt={file.fileName}
        zoom={zoom}
        container={canvas}
        onFit={setFitScale}
      />
    );
  } else {
    body = (
      <PdfView
        blob={state.blob}
        objectUrl={state.objectUrl}
        scale={scale}
        container={canvas}
        onFit={setFitScale}
        fit={zoom === 'fit'}
        onError={(error) => setState({ status: 'error', code: errorCode(error) })}
      />
    );
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={file.fileName}
      size="min(1120px, 100%)"
      fullScreen={narrow}
      centered
      classNames={{ title: classes.title, body: classes.modalBody }}
      closeButtonProps={{ 'aria-label': p('close') }}
    >
      <div className={classes.viewer}>
        <div className={classes.toolbar}>
          <div className={classes.zoom}>
            <ActionIcon
              variant="subtle"
              className={classes.tool}
              aria-label={p('zoomOut')}
              disabled={!ready || scale <= MIN_ZOOM}
              onClick={() => step(-1)}
            >
              <IconMinus size={16} />
            </ActionIcon>
            <output className={classes.zoomValue} aria-live="polite">
              {ready ? zoomText(scale, locale) : ''}
            </output>
            <ActionIcon
              variant="subtle"
              className={classes.tool}
              aria-label={p('zoomIn')}
              disabled={!ready || scale >= MAX_ZOOM}
              onClick={() => step(1)}
            >
              <IconPlus size={16} />
            </ActionIcon>
            <ActionIcon
              variant="subtle"
              className={classes.tool}
              aria-label={p('fit')}
              aria-pressed={zoom === 'fit'}
              disabled={!ready}
              onClick={() => setZoom('fit')}
            >
              <IconArrowAutofitWidth size={16} />
            </ActionIcon>
          </div>
          {download}
        </div>
        <section
          ref={canvas}
          className={classes.canvas}
          data-kind={ready ? state.kind : undefined}
          aria-label={p('dialog', { name: file.fileName })}
          // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
          tabIndex={0}
        >
          {body}
        </section>
      </div>
    </Modal>
  );
}

function ImageView({
  src,
  alt,
  zoom,
  container,
  onFit,
}: {
  src: string;
  alt: string;
  zoom: Zoom;
  container: RefObject<HTMLElement | null>;
  onFit: (scale: number) => void;
}) {
  const [natural, setNatural] = useState<{ width: number; height: number } | null>(null);

  // Sığdırma: resim pencereye sığar, büyütülmez
  const measure = useCallback(() => {
    const box = container.current;
    if (!box || !natural) return;
    const width = box.clientWidth - CANVAS_PADDING * 2;
    const height = box.clientHeight - CANVAS_PADDING * 2;
    onFit(Math.min(1, width / natural.width, height / natural.height));
  }, [container, natural, onFit]);
  useEffect(() => {
    measure();
    const box = container.current;
    if (!box || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(box);
    return () => observer.disconnect();
  }, [container, measure]);

  return (
    <img
      src={src}
      alt={alt}
      className={classes.image}
      data-fit={zoom === 'fit' || undefined}
      onLoad={(event) =>
        setNatural({
          width: event.currentTarget.naturalWidth || 1,
          height: event.currentTarget.naturalHeight || 1,
        })
      }
      style={
        zoom === 'fit' || !natural
          ? undefined
          : { width: natural.width * zoom, height: natural.height * zoom }
      }
    />
  );
}

function PdfView({
  blob,
  objectUrl,
  scale,
  fit,
  container,
  onFit,
  onError,
}: {
  blob: Blob;
  objectUrl: string;
  scale: number;
  fit: boolean;
  container: RefObject<HTMLElement | null>;
  onFit: (scale: number) => void;
  onError: (error: unknown) => void;
}) {
  const { t } = useTranslation();
  const [doc, setDoc] = useState<{
    handle: PdfHandle;
    first: { width: number; height: number };
  } | null>(null);
  const latest = useRef(onError);
  latest.current = onError;

  useEffect(() => {
    const controller = new AbortController();
    let opened: PdfHandle | null = null;
    // Parçalar bellekteki dosyadan okunur; ağa yeniden gidilmez
    openPdf(objectUrl, {
      fetch: blobRangeFetch(blob),
      signal: controller.signal,
      onFailure: (error) => latest.current(error),
    }).then(
      async (handle) => {
        opened = handle;
        const first = await handle.pageSize(1);
        if (!controller.signal.aborted) setDoc({ handle, first });
      },
      (error: unknown) => {
        if (!controller.signal.aborted) latest.current(error);
      },
    );
    return () => {
      controller.abort();
      opened?.destroy();
    };
  }, [blob, objectUrl]);

  // Sığdırma: ilk sayfanın genişliği pencereye
  useEffect(() => {
    const box = container.current;
    if (!box || !doc) return;
    const measure = () =>
      onFit(Math.max(MIN_ZOOM, (box.clientWidth - CANVAS_PADDING * 2) / doc.first.width));
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(box);
    return () => observer.disconnect();
  }, [container, doc, onFit]);

  if (!doc) {
    return (
      <div className={classes.message} role="status">
        <Loader size="sm" />
        <span>{t('attachmentPreview.loading')}</span>
      </div>
    );
  }
  const total = doc.handle.numPages;
  const shown = Math.min(total, MAX_PDF_PAGES);
  return (
    <div className={classes.pages} data-fit={fit || undefined}>
      {Array.from({ length: shown }, (_, index) => (
        <PdfPage
          // biome-ignore lint/suspicious/noArrayIndexKey: sayfa numarası sabittir
          key={index}
          handle={doc.handle}
          page={index + 1}
          total={total}
          scale={scale}
          size={doc.first}
        />
      ))}
      {total > shown ? (
        <p className={classes.note}>{t('attachmentPreview.pagesCut', { shown, total })}</p>
      ) : null}
    </div>
  );
}

function PdfPage({
  handle,
  page,
  total,
  scale,
  size,
}: {
  handle: PdfHandle;
  page: number;
  total: number;
  scale: number;
  size: { width: number; height: number };
}) {
  const { t } = useTranslation();
  const canvas = useRef<HTMLCanvasElement | null>(null);
  const [own, setOwn] = useState(size);

  useEffect(() => {
    let live = true;
    void handle.pageSize(page).then((value) => {
      if (live) setOwn(value);
    });
    return () => {
      live = false;
    };
  }, [handle, page]);

  useEffect(() => {
    const target = canvas.current;
    if (!target) return;
    const ratio = Math.min(globalThis.devicePixelRatio || 1, 2);
    const task = handle.render(page, target, scale, ratio);
    task.promise.catch(() => undefined);
    return () => task.cancel();
  }, [handle, page, scale]);

  return (
    <div
      className={classes.page}
      data-page={page}
      style={{ width: own.width * scale, height: own.height * scale }}
    >
      <canvas
        ref={canvas}
        className={classes.pageCanvas}
        role="img"
        aria-label={t('attachmentPreview.page', { n: page, total })}
      />
    </div>
  );
}
